import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Notification } from '../types';
import { useWebSocket } from './WebSocketContext';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  fetchNotifications: (options?: { cursor?: string; filters?: Record<string, any> }) => Promise<void>;
  loadMore: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  archiveNotification: (id: string) => Promise<void>;
  snoozeNotification: (id: string, duration: number) => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { socket } = useWebSocket();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const isFetchingRef = useRef(false);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (options?: { cursor?: string; filters?: Record<string, any> }) => {
    if (isFetchingRef.current) return;
    
    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Build query params
      const params = new URLSearchParams();
      if (options?.cursor) {
        params.append('cursor', options.cursor);
        params.append('limit', '50');
      } else {
        params.append('limit', '50');
      }
      
      // Add filters if provided
      if (options?.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
      }

      const response = await fetch(`${apiUrl}/api/notifications?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        // Transform dates from strings to Date objects
        const transformedNotifications = data.notifications.map((n: any) => {
          // Extract quoteId - handle both populated and non-populated cases
          let quoteId: string | undefined = undefined;
          if (n.quoteId) {
            if (typeof n.quoteId === 'object' && n.quoteId !== null) {
              quoteId = n.quoteId._id ? String(n.quoteId._id) : String(n.quoteId);
            } else {
              quoteId = String(n.quoteId);
            }
          }

          return {
            ...n,
            category: n.category || 'user',
            priority: n.priority || 'normal',
            deliveryStatus: n.deliveryStatus || 'delivered',
            createdAt: new Date(n.createdAt),
            updatedAt: n.updatedAt ? new Date(n.updatedAt) : undefined,
            readAt: n.readAt ? new Date(n.readAt) : undefined,
            quoteId: quoteId,
          };
        });
        
        if (options?.cursor) {
          // Append to existing notifications
          setNotifications(prev => [...prev, ...transformedNotifications]);
        } else {
          // Replace notifications
          setNotifications(transformedNotifications);
        }
        
        // Update cursor and hasMore
        if (data.cursor) {
          setNextCursor(data.cursor.next);
          setHasMore(data.cursor.hasMore);
        } else if (data.pagination) {
          setHasMore(data.pagination.hasMore);
          setNextCursor(null);
        }
        
        // Calculate unread count from notifications
        const count = transformedNotifications.filter((n: Notification) => !n.read).length;
        setUnreadCount(count);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setError('Failed to fetch notifications');
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [apiUrl]);

  // Load more notifications
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading || !nextCursor) return;
    await fetchNotifications({ cursor: nextCursor });
  }, [hasMore, isLoading, nextCursor, fetchNotifications]);

  // Refresh unread count only
  const refreshUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/api/notifications/unread-count`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setUnreadCount(data.count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [apiUrl]);

  // Mark notification as read
  const markAsRead = useCallback(async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/api/notifications/${id}/read`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        // Optimistically update local state
        setNotifications((prev) => {
          const updated = prev.map((notif) =>
            notif._id === id ? { ...notif, read: true, readAt: new Date() } : notif
          );
          // Update unread count
          const count = updated.filter((n) => !n.read).length;
          setUnreadCount(count);
          return updated;
        });
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, [apiUrl]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/api/notifications/mark-all-read`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        // Optimistically update local state
        setNotifications((prev) => {
          const now = new Date();
          return prev.map((notif) => ({ ...notif, read: true, readAt: now }));
        });
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [apiUrl]);

  // Delete notification
  const deleteNotification = useCallback(async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/api/notifications/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        // Optimistically update local state
        setNotifications((prev) => {
          const updated = prev.filter((notif) => notif._id !== id);
          // Update unread count
          const count = updated.filter((n) => !n.read).length;
          setUnreadCount(count);
          return updated;
        });
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }, [apiUrl]);

  // Archive notification
  const archiveNotification = useCallback(async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/api/notifications/${id}/archive`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        // Remove from list (archived notifications are hidden by default)
        setNotifications((prev) => {
          const updated = prev.filter((notif) => notif._id !== id);
          // Update unread count
          const count = updated.filter((n) => !n.read).length;
          setUnreadCount(count);
          return updated;
        });
      }
    } catch (error) {
      console.error('Failed to archive notification:', error);
    }
  }, [apiUrl]);

  // Snooze notification
  const snoozeNotification = useCallback(async (id: string, duration: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/api/notifications/${id}/snooze`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ duration }),
      });

      const data = await response.json();
      if (data.success) {
        // Remove from list (snoozed notifications are hidden until time expires)
        setNotifications((prev) => {
          const updated = prev.filter((notif) => notif._id !== id);
          // Update unread count
          const count = updated.filter((n) => !n.read).length;
          setUnreadCount(count);
          return updated;
        });
      }
    } catch (error) {
      console.error('Failed to snooze notification:', error);
    }
  }, [apiUrl]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Set up Socket.io listeners for real-time notifications
  useEffect(() => {
    if (!socket) return;

    const handleNewNotification = (data: {
      notification: Notification;
      timestamp: string;
    }) => {
      console.log('📨 [NOTIFICATION CONTEXT] Received new notification:', data.notification);
      
      // Transform notification data
      const notificationData: Notification = {
        ...data.notification,
        category: data.notification.category || 'user',
        priority: data.notification.priority || 'normal',
        deliveryStatus: data.notification.deliveryStatus || 'delivered',
        createdAt: new Date(data.notification.createdAt),
        updatedAt: data.notification.updatedAt
          ? new Date(data.notification.updatedAt)
          : undefined,
        readAt: data.notification.readAt
          ? new Date(data.notification.readAt)
          : undefined,
        quoteId: data.notification.quoteId
          ? String(data.notification.quoteId)
          : undefined,
      };

      // Add notification to the list (avoid duplicates)
      setNotifications((prev) => {
        const exists = prev.some((n) => n._id === notificationData._id);
        if (exists) {
          return prev;
        }
        // Add to the beginning of the list
        const updated = [notificationData, ...prev];
        
        // Update unread count
        const count = updated.filter((n) => !n.read).length;
        setUnreadCount(count);
        
        return updated;
      });
    };

    const handleNotificationUpdate = (data: {
      update: { unreadCount?: number };
      timestamp: string;
    }) => {
      console.log('🔄 [NOTIFICATION CONTEXT] Received notification update:', data.update);
      
      // Update unread count if provided
      if (data.update.unreadCount !== undefined) {
        setUnreadCount(data.update.unreadCount);
      } else {
        // Refresh from server
        refreshUnreadCount();
      }
    };

    socket.on('notification:new', handleNewNotification);
    socket.on('notification:update', handleNotificationUpdate);

    return () => {
      socket.off('notification:new', handleNewNotification);
      socket.off('notification:update', handleNotificationUpdate);
    };
  }, [socket, refreshUnreadCount]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    isLoading,
    error,
    hasMore,
    fetchNotifications,
    loadMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    archiveNotification,
    snoozeNotification,
    refreshUnreadCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
